import React, { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { ICourt, ISlotHistory, ISummaryItem } from "../../../types/api";
import {
  createBulkSlotWarning,
  createSlotWarning,
  getSlotHistory,
} from "../../../api/slotHistory/slotHistory";
import { formatDate, formatToIsoDate } from "../../../shared/dateUtils";
import { formatDateToSwedish } from "../../../shared/utility";
import "./CreateBulkWarningPopup.scss";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import cross from "../../../assets/images/cross.svg";
import { MultiSelect } from "../../../components/UI/MultiSelect/MultiSelect";
import WeekDaySelector from "../../../hoc/DatePicker/Datepicker";

interface ICreateBulkWarningPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  courts: ICourt[];
}

function CreateBulkWarningPopup({
  showPopup,
  onConfirm,
  onClose,
  courts,
}: ICreateBulkWarningPopupProps) {
  const [showCreateWarningPopup, setShowCreateWarningPopup] = useState(false);
  const [selectedCourts, setSelectedCourts] = useState<ICourt[]>(courts);
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState("07:00");
  const [endTime, setEndTime] = useState("22:00");
  const courtNames = courts.map((court) => court.name);
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(new Date());

  const handleClose = () => {
    setShowCreateWarningPopup(false);
    setWarning("");
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };

  const handleCreateWarning = async () => {
    const loadToast = toast.loading("Skapar varningar...");
    try {
      setLoading(true);

      const payload = {
        courts: selectedCourts.map((c) => c.court_id),
        start_date: selectedStartDate.toISOString().split("T")[0],
        end_date: selectedEndDate.toISOString().split("T")[0],
        start_time: startTime + ":00",
        end_time: endTime + ":00",
        comment: warning,
      };

      await createBulkSlotWarning(payload);

      setLoading(false);
      toast.dismiss(loadToast);
      toast.success("Varning skapad");
      handleConfirm();
    } catch (error) {
      toast.error("Kunde inte skapa varning");
      setLoading(false);
      toast.dismiss(loadToast);
    }
  };

  const timeIntervals = [
    "07:00",
    "07:30",
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
    "19:30",
    "20:00",
    "20:30",
    "21:00",
    "21:30",
    "22:00",
    "22:30",
  ];
  return (
    <Popup
      showPopup={showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Välj de banor och tider du vill skapa en varning för
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowCreateWarningPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field align-start">
              <div className="general-popup-flex-left text-m">
                <label>Datum:</label>
                <WeekDaySelector
                  value={selectedStartDate}
                  onChange={(value) => setSelectedStartDate(value)}
                  includeWeekdays={false}
                  textM
                  isSmall
                />
                till
                <WeekDaySelector
                  value={selectedEndDate}
                  onChange={(value) => setSelectedEndDate(value)}
                  includeWeekdays={false}
                  textM
                  isSmall
                />
              </div>
              <div className="general-popup-flex-left text-m">
                <label>Påverkade start tider:</label>
                <select
                  name="startTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="text-m"
                >
                  {timeIntervals.map((interval, index) => (
                    <option key={index} value={interval}>
                      {interval}
                    </option>
                  ))}
                </select>
                <span>till och med</span>
                <select
                  name="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="text-m"
                >
                  {timeIntervals.map((interval, index) => (
                    <option key={index} value={interval}>
                      {interval}
                    </option>
                  ))}
                </select>
              </div>
              <div className="general-popup-flex-left text-m">
                <label>Banor:</label>
                <MultiSelect
                  label="Banor"
                  options={courts.map((c) => ({
                    label: c.name,
                    value: c.court_id,
                  }))}
                  selected={selectedCourts.map((c) => c.court_id)}
                  multiple
                  onChange={(val) => {
                    const ids = val as string[];
                    setSelectedCourts(
                      courts.filter((c) => ids.includes(c.court_id)),
                    );
                  }}
                  formatSelected={(val) =>
                    `Banor (${(val as string[]).length})`
                  }
                  allButton
                  presetOptions={[
                    {
                      label: "Inne",
                      values: courts
                        .filter(
                          (c) =>
                            c.name === "CC" ||
                            (/^B\d+$/.test(c.name) &&
                              parseInt(c.name.slice(1)) >= 1 &&
                              parseInt(c.name.slice(1)) <= 15 &&
                              c.name !== "B12" &&
                              c.name !== "B13"),
                        )
                        .map((c) => c.court_id),
                    },
                    {
                      label: "Ute",
                      values: courts
                        .filter(
                          (c) =>
                            /^B\d+$/.test(c.name) &&
                            parseInt(c.name.slice(1)) >= 20 &&
                            parseInt(c.name.slice(1)) <= 27,
                        )
                        .map((c) => c.court_id),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="comment" className="text-m">
                Varningsmedelande{" "}
              </label>
              <input
                type="text"
                name="comment"
                id="comment"
                className="text-m"
                value={warning}
                onChange={(e) => setWarning(e.target.value)}
              />
            </div>
          </div>
          <div className="general-popup-bottom-buttons-wrapper flex-end">
            <button type="submit" className="text-m">
              Lägg till varning
            </button>
          </div>
        </form>
      </div>
      <HandleConfirmationPopup
        showPopup={showCreateWarningPopup}
        onClose={() => setShowCreateWarningPopup(false)}
        onConfirm={handleCreateWarning}
        confirmText="Skapa varning"
        denyText="Avbryt"
        questionText="Du kommer att skapa varningar för valda banor och tider. Är du säker på att du vill skapa dessa varningar? 
        Vid tomt meddelande tas eventuella befintliga varningar bort istället."
        loading={loading}
      />
    </Popup>
  );
}

export default CreateBulkWarningPopup;
